import { useTranslation } from "react-i18next";
import { Shield, Lock, CheckCircle2 } from "lucide-react";

const VisaIcon = () => (
  <svg viewBox="0 0 48 32" className="h-7 w-auto" aria-hidden="true">
    <rect
      width="47"
      height="31"
      x="0.5"
      y="0.5"
      rx="2"
      fill="white"
      stroke="currentColor"
      strokeOpacity="0.15"
    />
    <path
      d="M19.5 21h-2.7l1.7-10.5h2.7L19.5 21zm-5.1 0l-2.6-7.2-.3-1.5c-.5-1.3-1.7-2-2.9-2h-4.4l-.1.4c1.5.4 2.8 1 3.9 1.8l2.7 8.5h2.8l4.3-10.5h-2.8l-2.6 10.5h2zm16.3-6.8c0-.9.7-1.4 2-1.4.9 0 1.6.2 2.1.4l.4-2.1c-.6-.2-1.5-.4-2.5-.4-2.6 0-4.5 1.4-4.5 3.4 0 1.5 1.3 2.3 2.3 2.8 1 .5 1.4.9 1.4 1.3 0 .7-.8 1.1-1.6 1.1-1 0-1.9-.2-2.6-.5l-.4 2.1c.7.3 1.8.5 3 .5 2.8 0 4.6-1.4 4.6-3.5 0-2.7-3.7-2.9-3.7-4.3zm13.2 6.8l-2.1-10.5h-2.2c-.7 0-1.3.4-1.5 1l-4.2 9.5h2.9l.6-1.6h3.6l.3 1.6h2.6zm-3.4-4.1l1.5-4 .8 4h-2.3z"
      fill="currentColor"
    />
  </svg>
);

const MastercardIcon = () => (
  <svg viewBox="0 0 48 32" className="h-7 w-auto" aria-hidden="true">
    <rect
      width="47"
      height="31"
      x="0.5"
      y="0.5"
      rx="2"
      fill="white"
      stroke="currentColor"
      strokeOpacity="0.15"
    />
    <circle cx="19" cy="16" r="7" fill="currentColor" opacity="0.95" />
    <circle cx="29" cy="16" r="7" fill="currentColor" opacity="0.6" />
  </svg>
);

const AmexIcon = () => (
  <svg viewBox="0 0 48 32" className="h-7 w-auto" aria-hidden="true">
    <rect
      width="47"
      height="31"
      x="0.5"
      y="0.5"
      rx="2"
      fill="white"
      stroke="currentColor"
      strokeOpacity="0.15"
    />
    <text
      x="24"
      y="19"
      textAnchor="middle"
      fill="currentColor"
      fontSize="7"
      fontWeight="700"
      fontFamily="Inter Tight, sans-serif"
      letterSpacing="0.5"
    >
      AMEX
    </text>
  </svg>
);

const trustBadges = [
  { id: "secure", icon: Lock, label: "SSL Encrypted" },
  { id: "verified", icon: CheckCircle2, label: "Verified Carrier" },
  { id: "protected", icon: Shield, label: "Buyer Protected" },
];

/**
 * Flat, editorial trust strip. Ditches beige/khaki AI-slop palette —
 * uses hairline rules and tokens instead.
 */
export function TrustSection() {
  const { t } = useTranslation();

  return (
    <section
      id="about"
      className="relative py-14 md:py-16 bg-stone-50 border-b hairline-strong scroll-mt-20"
      aria-labelledby="trust-heading"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-10 md:gap-16 items-center">
          {/* Left — travel safe */}
          <div>
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 bg-brand" aria-hidden="true" />
              <span className="text-eyebrow uppercase text-ink-muted">
                {t("landing.travelSafe")}
              </span>
            </div>
            <h2
              id="trust-heading"
              className="font-display text-[1.5rem] md:text-[1.75rem] text-ink mt-3 leading-snug max-w-md"
            >
              {t(
                "landing.travelSafeDescription",
                "Every booking is protected end-to-end — encrypted at purchase, verified at boarding.",
              )}
            </h2>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6">
              {trustBadges.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center gap-2 text-ink-muted"
                >
                  <b.icon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="text-eyebrow uppercase">
                    {t(`landing.trust.${b.id}`, b.label)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Vertical hairline on desktop */}
          <div
            className="hidden md:block h-24 w-px bg-hairline-strong"
            aria-hidden="true"
          />

          {/* Right — payments */}
          <div>
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 bg-ink" aria-hidden="true" />
              <span className="text-eyebrow uppercase text-ink-muted">
                {t("landing.paymentMethods", "Payment Methods")}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-4 text-ink">
              <VisaIcon />
              <MastercardIcon />
              <AmexIcon />
            </div>
            <p className="text-xs text-ink-subtle mt-4 leading-relaxed">
              {t(
                "landing.securePayments",
                "Payments processed securely by Stripe. Your card details never touch our servers.",
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TrustSection;
